import { useMemo } from 'react';
import { ApolloProvider } from '@apollo/client/react';
import { ThemeProvider, theme as defaultTheme } from '@slope/uikit';
import { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import { useControls, folder, button } from 'leva';
import Color from 'colorjs.io';
import { createStore, StoreProvider } from '@slope/store';
import { RouterProvider } from 'react-router';
import { ApolloExplorer } from './components/ApolloExplorer';
import { toggleFullScreen } from './utils';
import { ScoringCalcMode } from '@slope/graphql/resolvers';

export type AppProps = React.ComponentProps<typeof RouterProvider> & {
  client: ApolloClient<NormalizedCacheObject>;
};

export const App: React.FC<AppProps> = ({ client, router }) => {
  const { primary, secondary, show } = useControls({
    colors: folder({
      primary: defaultTheme.colors.primary.main,
      secondary: defaultTheme.colors.secondary.main,
    }),
    explorer: folder({
      show: false,
    }),
    fullscreen: button(() => {
      toggleFullScreen();
    }),
  });

  const theme = useMemo(
    () => ({
      colors: {
        primary: {
          main: primary,
          dark: new Color(primary).darken(0.2).toString(),
          light: new Color(primary).lighten(0.2).toString(),
        },
        secondary: {
          main: secondary,
          dark: new Color(secondary).darken(0.2).toString(),
          light: new Color(secondary).lighten(0.2).toString(),
        },
      },
    }),
    [primary, secondary],
  );

  const params = new URLSearchParams(document.location.search);

  const store = useMemo(
    () =>
      createStore({
        ui: {
          liveScoring: {
            enabled: params.has('ls') || true,
            calcMode:
              ScoringCalcMode[params.get('lscm') as keyof typeof ScoringCalcMode] ??
              ScoringCalcMode.Unspecified,
          },
          panels: {
            enabled: params.has('p') || true,
          },
          highlights: {
            enabled: params.has('h') || true,
          },
        },
        origin: { lat: 0, lng: 0 },
        highlights: [],
        highlight: null,
        course: null,
        hole: null,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <ApolloProvider client={client}>
      <StoreProvider store={store}>
        <ThemeProvider theme={theme}>
          {show && (
            <div style={{ position: 'fixed', inset: 0, zIndex: 999, height: '100vh' }}>
              <ApolloExplorer />
            </div>
          )}
          <RouterProvider router={router} />
        </ThemeProvider>
      </StoreProvider>
    </ApolloProvider>
  );
};
